import { Fragment } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { GefahrIcon } from "./GefahrIcon";
import {
  GEFAHREN_ROWS,
  formatThousands,
  onlyDigits,
  vorgabeHint,
  type GefahrBetragKey,
  type GefahrenFormState,
  type GefahrFormInput,
  type GefahrRow,
} from "./gefahrenConfig";

/**
 * Versicherte Gefahren als EINE Tabelle: je Gefahr ankreuzen, ob sie versichert
 * werden soll, dann Höchstentschädigung, Jahreshöchstentschädigung und
 * Selbstbeteiligung (jeweils €) sowie ob ein Mietverlust vereinbart wird.
 *
 * Bei einem Fremdvertrag stehen Vorvertrag und neuer Vertrag als Spaltenpaare
 * nebeneinander, damit sich beide Stände direkt in einer Zeile vergleichen lassen.
 */

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  intro: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.muted,
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
  },
  th: {
    textAlign: "left",
    fontWeight: 700,
    fontSize: "0.7rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    whiteSpace: "nowrap",
  },
  thGroup: {
    textAlign: "center",
    ...shorthands.borderLeft("1px", "solid", BRAND.borderDashed),
  },
  thSub: {
    textAlign: "center",
    fontSize: "0.66rem",
    textTransform: "none",
    letterSpacing: "0.02em",
    paddingTop: "6px",
    paddingBottom: "6px",
  },
  thGefahr: {
    minWidth: "260px",
  },
  groupRow: {
    backgroundColor: "#f7f2f8",
  },
  groupCell: {
    fontFamily: BRAND.fontPrimary,
    fontWeight: 700,
    fontSize: "0.82rem",
    letterSpacing: "0.02em",
    color: BRAND.purple,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  td: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    color: BRAND.ink,
    verticalAlign: "middle",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  tdGroupStart: {
    ...shorthands.borderLeft("1px", "solid", BRAND.borderDashed),
  },
  tdCenter: {
    textAlign: "center",
  },
  gefahrZelle: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
  },
  iconBox: {
    width: "26px",
    height: "26px",
    flexShrink: 0,
    marginTop: "1px",
    backgroundColor: "#f5eef6",
    color: BRAND.purple,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.borderRadius("7px"),
  },
  iconBoxActive: {
    backgroundColor: BRAND.purple,
  },
  gefahrName: {
    fontWeight: 600,
    lineHeight: 1.35,
  },
  gefahrNameSub: {
    paddingLeft: "18px",
    ...shorthands.borderLeft("2px", "solid", BRAND.gold),
  },
  hint: {
    display: "block",
    fontSize: "0.72rem",
    color: BRAND.muted,
    marginTop: "2px",
    lineHeight: 1.35,
  },
  rowActive: {
    backgroundColor: "#faf6fb",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: BRAND.purple,
  },
  euroWrap: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
  },
  euroInput: {
    width: "100%",
    minWidth: "104px",
    maxWidth: "150px",
    boxSizing: "border-box",
    textAlign: "right",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontVariantNumeric: "tabular-nums",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      outlineStyle: "none",
      boxShadow: "0 0 0 3px rgba(77,32,81,0.12)",
    },
    ":disabled": {
      backgroundColor: "#f6f4f7",
      color: "#b6afbb",
      cursor: "not-allowed",
    },
  },
  euroSuffix: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  colVorvertrag: {
    backgroundColor: "#fbfafc",
  },
});

/** Zahleneingabe in €: Anzeige optional mit Tausenderpunkten, State = reine Ziffern. */
const EuroInput = ({
  value,
  onChange,
  disabled,
  thousands,
  ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  thousands: boolean;
  ariaLabel: string;
}) => {
  const styles = useStyles();
  const digits = onlyDigits(value ?? "");
  return (
    <span className={styles.euroWrap}>
      <input
        className={styles.euroInput}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-label={ariaLabel}
        disabled={disabled}
        value={thousands ? formatThousands(digits) : digits}
        onChange={(e) => onChange(onlyDigits(e.target.value))}
        placeholder={disabled ? "—" : "0"}
      />
      <span className={styles.euroSuffix}>€</span>
    </span>
  );
};

const BETRAG_SPALTEN: Array<{ key: GefahrBetragKey; label: string; thousands: boolean }> = [
  { key: "hoechstentschaedigung", label: "Höchstentschädigung", thousands: true },
  { key: "jahreshoechstentschaedigung", label: "Jahreshöchstentschädigung", thousands: true },
  { key: "selbstbeteiligung", label: "Selbstbeteiligung", thousands: false },
];

type Side = {
  state: GefahrenFormState;
  onChange: (rowId: string, patch: Partial<GefahrFormInput>) => void;
  vorvertrag: boolean;
};

/** Alle Eingabezellen einer Vertragsseite (Vorvertrag oder neuer Vertrag). */
const SideCells = ({ rowId, side, groupStart }: { rowId: string; side: Side; groupStart: boolean }) => {
  const styles = useStyles();
  const input = side.state[rowId] ?? {};
  const versichert = input.sollVersichertWerden === "ja";
  const cellClass = mergeClasses(styles.td, side.vorvertrag && styles.colVorvertrag);

  return (
    <>
      <td className={mergeClasses(cellClass, styles.tdCenter, groupStart && styles.tdGroupStart)}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={versichert}
          aria-label="Soll versichert werden"
          onChange={(e) =>
            side.onChange(
              rowId,
              e.target.checked
                ? { sollVersichertWerden: "ja" }
                : {
                    sollVersichertWerden: undefined,
                    hoechstentschaedigung: "",
                    jahreshoechstentschaedigung: "",
                    selbstbeteiligung: "",
                    mietverlust: undefined,
                  },
            )
          }
        />
      </td>
      {BETRAG_SPALTEN.map((col) => (
        <td key={col.key} className={cellClass}>
          <EuroInput
            ariaLabel={col.label}
            value={input[col.key] ?? ""}
            thousands={col.thousands}
            disabled={!versichert}
            onChange={(next) => side.onChange(rowId, { [col.key]: next })}
          />
        </td>
      ))}
      <td className={mergeClasses(cellClass, styles.tdCenter)}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={input.mietverlust === "ja"}
          disabled={!versichert}
          aria-label="Mietverlust vereinbaren"
          onChange={(e) => side.onChange(rowId, { mietverlust: e.target.checked ? "ja" : undefined })}
        />
      </td>
    </>
  );
};

const GefahrZeile = ({
  id,
  iconKey,
  label,
  hint,
  isSub,
  sides,
}: {
  id: string;
  iconKey: string;
  label: string;
  hint: string;
  isSub: boolean;
  sides: Side[];
}) => {
  const styles = useStyles();
  const active = sides.some((s) => s.state[id]?.sollVersichertWerden === "ja");
  return (
    <tr className={active ? styles.rowActive : undefined}>
      <td className={styles.td}>
        <div className={mergeClasses(styles.gefahrZelle, isSub && styles.gefahrNameSub)}>
          <span className={mergeClasses(styles.iconBox, active && styles.iconBoxActive)}>
            <GefahrIcon id={iconKey} color={active ? BRAND.white : BRAND.purple} size={16} />
          </span>
          <span className={styles.gefahrName}>
            {label}
            {hint && <span className={styles.hint}>{hint}</span>}
          </span>
        </div>
      </td>
      {sides.map((side, idx) => (
        <SideCells key={idx} rowId={id} side={side} groupStart={sides.length > 1} />
      ))}
    </tr>
  );
};

export const GefahrenTabelle = ({
  state,
  onChange,
  vorvertragState,
  onChangeVorvertrag,
}: {
  state: GefahrenFormState;
  onChange: (rowId: string, patch: Partial<GefahrFormInput>) => void;
  /** Nur bei Fremdvertrag gesetzt — dann erscheinen die Vorvertrags-Spalten. */
  vorvertragState?: GefahrenFormState;
  onChangeVorvertrag?: (rowId: string, patch: Partial<GefahrFormInput>) => void;
}) => {
  const styles = useStyles();
  const compare = !!vorvertragState && !!onChangeVorvertrag;

  const sides: Side[] = compare
    ? [
        { state: vorvertragState!, onChange: onChangeVorvertrag!, vorvertrag: true },
        { state, onChange, vorvertrag: false },
      ]
    : [{ state, onChange, vorvertrag: false }];

  const spaltenProSeite = 1 + BETRAG_SPALTEN.length + 1;
  const gesamtSpalten = 1 + sides.length * spaltenProSeite;

  return (
    <div className={styles.root}>
      <p className={styles.intro}>
        Kreuzen Sie je Gefahr an, ob sie versichert werden soll, und tragen Sie
        Höchstentschädigung, Jahreshöchstentschädigung und Selbstbeteiligung in Euro ein.
        Zuletzt legen Sie fest, ob für die Gefahr ein Mietverlust vereinbart wird.
        {compare && " Links steht der Vorvertrag, rechts der neue Vertrag."}
      </p>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            {compare && (
              <tr>
                <th className={mergeClasses(styles.th, styles.thGefahr)} rowSpan={2}>
                  Versicherte Gefahr
                </th>
                {sides.map((side, idx) => (
                  <th
                    key={idx}
                    className={mergeClasses(styles.th, styles.thGroup)}
                    colSpan={spaltenProSeite}
                  >
                    {side.vorvertrag ? "Vorvertrag" : "Neuer Vertrag"}
                  </th>
                ))}
              </tr>
            )}
            <tr>
              {!compare && (
                <th className={mergeClasses(styles.th, styles.thGefahr)}>Versicherte Gefahr</th>
              )}
              {sides.map((_side, idx) => (
                <Fragment key={idx}>
                  <th
                    className={mergeClasses(
                      styles.th,
                      styles.tdCenter,
                      compare && styles.thSub,
                      compare && styles.thGroup,
                    )}
                  >
                    Versichert
                  </th>
                  {BETRAG_SPALTEN.map((col) => (
                    <th
                      key={col.key}
                      className={mergeClasses(styles.th, compare && styles.thSub)}
                    >
                      {compare
                        ? col.key === "hoechstentschaedigung"
                          ? "HE (€)"
                          : col.key === "jahreshoechstentschaedigung"
                            ? "JHE (€)"
                            : "SB (€)"
                        : `${col.label} (€)`}
                    </th>
                  ))}
                  <th className={mergeClasses(styles.th, styles.tdCenter, compare && styles.thSub)}>
                    Mietverlust
                  </th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {GEFAHREN_ROWS.map((row: GefahrRow) => {
              const hasSubRows = !!row.subRows && row.subRows.length > 0;
              if (!hasSubRows) {
                return (
                  <GefahrZeile
                    key={row.id}
                    id={row.id}
                    iconKey={row.id}
                    label={row.versicherteGefahr}
                    hint={vorgabeHint(row)}
                    isSub={false}
                    sides={sides}
                  />
                );
              }
              return [
                <tr key={`${row.id}-group`} className={styles.groupRow}>
                  <td className={styles.groupCell} colSpan={gesamtSpalten}>
                    {row.versicherteGefahr}
                  </td>
                </tr>,
                ...row.subRows!.map((sub) => (
                  <GefahrZeile
                    key={sub.id}
                    id={sub.id}
                    iconKey={row.id}
                    label={sub.bezeichnung}
                    hint={vorgabeHint({
                      hoechstentschaedigung: sub.hoechstentschaedigung,
                      jahreshoechstentschaedigung: row.jahreshoechstentschaedigung,
                      selbstbehalt: sub.selbstbehalt,
                    })}
                    isSub
                    sides={sides}
                  />
                )),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
